import { IItem } from "../types";

// CSV Configuration
const CSV_CONFIG = {
  VALID_FIELDS_COUNT: 3,
  SKU_INDEX: 0,
  QUANTITY_INDEX: 1,
  REFERENCE_INDEX: 2,
} as const;

/**
 * Constructs JSON objects from CSV data
 * @param data - 2D array of CSV data
 * @returns Array of IItem objects
 */
export const constructJSON = (data: string[][]): IItem[] => {
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }

  return data.map((row, index) => {
    if (!row || row.length < CSV_CONFIG.VALID_FIELDS_COUNT) {
      throw new Error(`Invalid CSV row at index ${index}: insufficient fields`);
    }

    const sku = row[CSV_CONFIG.SKU_INDEX]?.trim();
    const quantityStr = row[CSV_CONFIG.QUANTITY_INDEX];
    const reference = row[CSV_CONFIG.REFERENCE_INDEX]?.trim() || "";

    if (!sku || !quantityStr) {
      throw new Error(`Invalid CSV row at index ${index}: missing SKU or quantity`);
    }

    const quantity = parseInt(quantityStr, 10);
    if (isNaN(quantity) || quantity <= 0) {
      throw new Error(`Invalid CSV row at index ${index}: invalid quantity`);
    }

    return {
      sku,
      quantity,
      reference,
    };
  });
};

/**
 * Validates CSV data for errors
 * @param data - 2D array of CSV data
 * @returns Array of line numbers with errors (1-indexed)
 */
export const checkCSVForErrors = (data: string[][]): number[] => {
  if (!Array.isArray(data)) {
    return [];
  }

  const failed: number[] = [];

  data.forEach((row, index) => {
    const lineNumber = index + 1;

    // Check if row has sufficient fields
    if (!row || row.length < CSV_CONFIG.VALID_FIELDS_COUNT) {
      failed.push(lineNumber);
      return;
    }

    const sku = row[CSV_CONFIG.SKU_INDEX];
    const quantityStr = row[CSV_CONFIG.QUANTITY_INDEX];

    // Check if required fields are present
    if (!sku || !quantityStr) {
      failed.push(lineNumber);
      return;
    }

    // Check if quantity is a valid number
    const quantity = parseInt(quantityStr, 10);
    if (isNaN(quantity) || quantity <= 0) {
      failed.push(lineNumber);
      return;
    }
  });

  return failed;
};

/**
 * Removes duplicate SKUs and combines quantities
 * @param items - Array of items to deduplicate
 * @returns Array of unique items with combined quantities
 */
export const removeSkuDuplicates = (items: IItem[]): IItem[] => {
  if (!Array.isArray(items)) {
    return [];
  }

  const cleanItems: IItem[] = [];
  const skuMap = new Map<string, number>();

  items.forEach((item) => {
    const existingIndex = cleanItems.findIndex((cleanItem) => cleanItem.sku === item.sku);

    if (existingIndex === -1) {
      // New item
      cleanItems.push({ ...item });
      skuMap.set(item.sku, cleanItems.length - 1);
    } else {
      // Duplicate SKU - combine quantities
      cleanItems[existingIndex].quantity += item.quantity;
    }
  });

  return cleanItems;
};

/**
 * Validates a single CSV row
 * @param row - Single CSV row
 * @param lineNumber - Line number for error reporting
 * @returns Validation result
 */
export const validateCSVRow = (
  row: string[],
  lineNumber: number,
): { isValid: boolean; error?: string } => {
  if (!row || row.length < CSV_CONFIG.VALID_FIELDS_COUNT) {
    return {
      isValid: false,
      error: `Line ${lineNumber}: Insufficient fields. Expected ${CSV_CONFIG.VALID_FIELDS_COUNT}, got ${row?.length || 0}`,
    };
  }

  const sku = row[CSV_CONFIG.SKU_INDEX];
  const quantityStr = row[CSV_CONFIG.QUANTITY_INDEX];

  if (!sku || !quantityStr) {
    return {
      isValid: false,
      error: `Line ${lineNumber}: Missing required fields (SKU or quantity)`,
    };
  }

  const quantity = parseInt(quantityStr, 10);
  if (isNaN(quantity) || quantity <= 0) {
    return {
      isValid: false,
      error: `Line ${lineNumber}: Invalid quantity. Must be a positive number`,
    };
  }

  return { isValid: true };
};
